import React, { useReducer, useState, useEffect } from 'react';
import { DateRangePicker, Label } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { validateDateRangeSelector } from '../../Validations/Validations';
import { parseHtml } from '../../utils/util';

const DlsDateRangePicker = ({
  cssProperties,
  fieldId,
  fieldRank,
  fieldLabel,
  fieldValidation,
  validations = [],
  dateFormat,
  formSubmissionPayload,
  fieldPlaceholder,
  defaultDates,
  minDays = 1,
  canRetainEndDateOnChange = true,
  isShowLegend = true, // can show legends if any
  dateIds,
  setValidationErrors,
  validationErrors,
  setFocus,
}) => {
  // get default start, end, min. max etc from JSON if any
  const [dates, setDates] = useState({ ...defaultDates });
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true, elementToFocus: '' }
  );

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: validationErrState.elementToFocus },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    // back button support
    if (formSubmissionPayload) {
      if (formSubmissionPayload[dateIds.start]) {
        setDates((oldState) => ({ ...oldState, start: formSubmissionPayload[dateIds.start] }));
      } else {
        setDates((oldState) => ({ ...oldState, start: '' }));
      }
      if (formSubmissionPayload[dateIds.end]) {
        setDates((oldState) => ({ ...oldState, end: formSubmissionPayload[dateIds.end] }));
      } else {
        setDates((oldState) => ({ ...oldState, end: '' }));
      }
    }
  }, [dateIds]);

  const handleKeyPress = (event) => {
    const validKey = event.key === 'Backspace'
      || event.key === 'Delete'
      || event.key === 'Tab'
      || /[\d/]/.test(event.key);
    if (!validKey) {
      event.preventDefault();
    }
  };

  // refactor
  const onDateChange = (event, date) => {
    // gets called two time by start date input and end date input --> default dls behaviour
    let startDate = dates.start ? dates.start : null;
    let endDate = dates.end ? dates.end : null;
    if (date && event.target.name === dateIds.start) {
      startDate = date.dateString;
      setDates((oldState) => ({ ...oldState, start: date.dateString }));
    } else if (date && event.target.name === dateIds.end) {
      endDate = date.dateString;
      setDates((oldState) => ({ ...oldState, end: date.dateString }));
    }

    if (validations.length > 0) {
      const validationStatus = validateDateRangeSelector({
        startDate, endDate, validations, dateIds, dateFormat,
      });
      if (validationStatus.invalidMsg) {
        dispatchErrState({
          validationErr: validationStatus.invalidMsg,
          isValid: false,
          elementToFocus: validationStatus.elementToFocus,
        });
      } else {
        dispatchErrState({ validationErr: '', isValid: true, elementToFocus: '' });
      }
    } else {
      dispatchErrState({ validationErr: '', isValid: true, elementToFocus: '' });
    }
  };

  // configure invalid handler
  // setFocus Pending
  const handleOnInvalid = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      onDateChange(e, null);
      setFocus({ ...validationErrors, ...{ [fieldRank]: dateIds.start } });
    }
  };

  return (
    <div className={cssProperties.style}>
      {
        fieldLabel
        && fieldLabel.label
        && (
          <Label
            htmlFor={fieldId}
            id={`${fieldId}-label`}
            className={fieldLabel.cssProperties.style}
          >
            {parseHtml(fieldLabel.label)}
            {!fieldValidation.required && (
              <span className={fieldLabel.cssProperties.style}>{fieldLabel.optionalLabel}</span>
            )}
          </Label>
        )
      }
      {fieldLabel
        && fieldLabel.subLabel
        && (
          <div>
            <Label
              htmlFor={fieldId}
              id={`${fieldId}-sublabel`}
              className={fieldLabel.cssProperties.subLabelStyle}
            >
              {parseHtml(fieldLabel.subLabel)}
            </Label>
          </div>
        )}
      <DateRangePicker
        id={fieldId}
        ids={dateIds}
        dates={dates}
        onKeyDown={handleKeyPress}
        showLegend={isShowLegend}
        footer={fieldLabel.calendarFooter}
        labels={fieldLabel.accessibilityLabels}
        onChange={onDateChange}
        canRetainEndDateOnChange={canRetainEndDateOnChange}
        minDays={minDays}
        status={validationErrState.isValid ? 'default' : 'error'}
        format={dateFormat}
        placeholder={fieldPlaceholder}
      />
      {fieldValidation
        && fieldValidation.required
        && (!dates.start || !dates.end) && (
          <div className="row hidden">
            <input required={true} onInvalid={handleOnInvalid} />
          </div>
      )}
      {validationErrState.validationErr && (
        <div
          id="alert-error"
          className="alert dls-color-warning pad-1-t col-md-12"
        >
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{validationErrState.validationErr}</span>
        </div>
      )}
    </div>
  );
};

DlsDateRangePicker.propTypes = {
  fieldId: PropTypes.string,
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  dateFormat: PropTypes.string,
  fieldPlaceholder: PropTypes.string,
  defaultDates: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  minDays: PropTypes.number,
  dateIds: PropTypes.objectOf(PropTypes.shape),
  canRetainEndDateOnChange: PropTypes.bool,
  isShowLegend: PropTypes.bool,
};

export default DlsDateRangePicker;
